import { useEffect, useRef, useState } from "react";

/**
 * CAPTCHA widget abstraction. If VITE_CAPTCHA_SITE_KEY is configured, loads
 * the hCaptcha script and renders the real widget. Otherwise (local
 * development / tests), renders a simple manual checkbox that produces a
 * fixed development token accepted only by the backend's local CAPTCHA
 * adapter (which itself refuses to run when ENVIRONMENT=production).
 */
let captchaLoader;

function loadCaptcha() {
  if (window.hcaptcha) return Promise.resolve(window.hcaptcha);
  if (!captchaLoader) {
    captchaLoader = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      window.hslhCaptchaReady = () => resolve(window.hcaptcha);
      script.src = "https://js.hcaptcha.com/1/api.js?onload=hslhCaptchaReady&render=explicit";
      script.async = true;
      script.onerror = () => {
        script.remove();
        captchaLoader = undefined;
        reject(new Error("Unable to load verification."));
      };
      document.body.appendChild(script);
    });
  }
  return captchaLoader;
}

export default function CaptchaWidget({ onToken, resetKey = 0 }) {
  const siteKey = import.meta.env.VITE_CAPTCHA_SITE_KEY;
  const containerRef = useRef(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;
    let widgetId;
    setLoadError(false);
    onToken("");
    loadCaptcha().then((captcha) => {
      if (cancelled || !containerRef.current) return;
      widgetId = captcha.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token) => onToken(token),
        "expired-callback": () => onToken(""),
        "error-callback": () => { onToken(""); setLoadError(true); },
      });
    }).catch(() => {
      if (!cancelled) { onToken(""); setLoadError(true); }
    });
    return () => {
      cancelled = true;
      if (widgetId !== undefined) window.hcaptcha.remove(widgetId);
    };
  }, [siteKey, onToken, resetKey]);

  if (siteKey) {
    return <>
      <div ref={containerRef} data-testid="captcha-widget" />
      {loadError && <p role="alert">Verification could not load. Refresh the page and try again.</p>}
    </>;
  }

  if (!import.meta.env.DEV) {
    return <p role="alert">Verification is temporarily unavailable. Please try again later.</p>;
  }

  return (
    <div className="checkbox-field">
      <input
        type="checkbox"
        id="dev-captcha"
        onChange={(e) => onToken(e.target.checked ? "local-test-token" : "")}
      />
      <label htmlFor="dev-captcha">I&rsquo;m not a robot (development verification)</label>
    </div>
  );
}
